import type { Customer } from './types';

// Dữ liệu mẫu của Mint-CustomerDetail (khách GREENBUD) — đúng dòng / thứ tự của board.
// Tiền (công nợ, hạn mức) không nằm ở đây: người xem chưa có quyền `money` chỉ thấy trạng thái.

export interface DetailJob {
  code: string;
  kind: 'RF' | 'TR';
  route: string;
  box: string;
  etd: string;
  status: string;
  sale: string;
  pic: string;
}
/** Dòng tab Job (đủ nhận hàng · tàu · số cont) */
export interface JobRow {
  code: string;
  kind: 'RF' | 'TR';
  route: string;
  /** nhận hàng · tàu / xe */
  cnee: string;
  vessel: string;
  box: string;
  etd: string;
  status: string;
  invoiced?: boolean;
  /** PIC chia %, vd "DAVID 70% · TUANNT 30%" */
  sale: string;
}
export type RefundState = 'ready' | 'wait' | 'done';
export interface RefundRow {
  job: string;
  what: string;
  state: RefundState;
}
export type JobSeg = 'all' | 'run' | 'rel' | 'inv';
export const JOB_SEGS: { key: JobSeg; label: string }[] = [
  { key: 'all', label: 'Tất cả' },
  { key: 'run', label: 'Đang chạy' },
  { key: 'rel', label: 'Đã release' },
  { key: 'inv', label: 'Đã xuất HĐ' },
];
const RUNNING = ['RQ Request', 'RQ Standby', 'BK Standby', 'BK Confirmed', 'BL Hold', 'BL Wait RLS'];
export function jobSegOf(j: JobRow, seg: JobSeg): boolean {
  if (seg === 'run') return !j.invoiced && RUNNING.includes(j.status);
  if (seg === 'rel') return !j.invoiced && j.status === 'BL Released';
  if (seg === 'inv') return !!j.invoiced;
  return true;
}
export const REFUND_LABEL: Record<RefundState, string> = {
  ready: 'Hãng đã hoàn · chờ trả khách',
  wait: 'Chờ hãng hoàn',
  done: 'Đã trả khách 10/Oct/2026',
};

export interface DetailRoute {
  lane: string;
  carriers: string;
  jobs: number;
  teu: number;
}
export interface DetailInteraction {
  channel: 'Zalo' | 'ZNS';
  who: string;
  at: string;
  by: string;
  text: string;
  /** lỗi gửi (ZNS bị chặn) */
  failed?: boolean;
}
/** dòng tab Tương tác (TblEntityInteraction) */
export const IX_KINDS = ['Tất cả', 'Gọi', 'Gặp', 'Mail', 'Zalo', 'ZNS', 'Mail MKT', 'Quà'] as const;
export type IxKind = (typeof IX_KINDS)[number];
export interface IxRow {
  kind: Exclude<IxKind, 'Tất cả'>;
  who: string;
  note: string;
  at: string;
  by: string;
  /** việc theo dõi kèm theo; warn = tới hạn / lỗi */
  fu?: string;
  fuTone?: 'warn' | 'lb';
}

export interface DetailTodo {
  title: string;
  owner: string;
  due: string;
  action: string;
}
export interface DetailPerson {
  name: string;
  role: string;
}

/** Loại thư / thông báo tự động mỗi liên hệ có thể nhận (cột tick của tab Liên hệ). */
export const MAIL_FLAGS = ['BKCF', 'B/L', 'AN', 'Debit', 'Hoá đơn', 'Thanh toán'] as const;
export type MailFlag = (typeof MAIL_FLAGS)[number];

export interface CfgContract { name: string; desc: string; status: string; ok: boolean }
export type ZnsTpl = 'ok' | 'wait' | 'rej';
export interface ZnsRow { key: string; name: string; tid: string; tpl: ZnsTpl; on: boolean; acc?: boolean; who: string[] }
export interface BankRow { bank: string; no: string; holder: string; isDefault: boolean; verified: boolean; by: string; verifiedBy: string }

export interface ContactRow {
  name: string;
  role: string;
  owner?: boolean;
  phone: string;
  mail: string;
  zalo: 'follow' | 'unfollow' | 'none';
  flags: MailFlag[];
}

export interface WikiNote { title: string; body: string; meta: string }
export interface WikiFile { name: string; kind: string; size: string; meta: string }

export interface CustomerDetailData {
  id: string;
  code: string;
  group: string;
  since: string;
  source: string;
  portCommon: string;
  carrierCommon: string;
  updated: string;
  updatedBy: string;
  tags: string[];
  jobsTotal: number;
  /** job 90 ngày (KPI đầu trang) */
  jobs90: number;
  jobs: DetailJob[];
  routes: DetailRoute[];
  /** toàn bộ job của khách (tab Job) */
  jobRows: JobRow[];
  refunds: RefundRow[];
  /** kim ngạch xuất 12 tháng (nghìn USD) theo tờ khai hải quan; null = chưa nạp */
  exports: { month: string; value: number | null }[];
  exportTotal: string;
  exportDelta: string;
  hanaShare: string;
  declarations: number;
  lots: number;
  legal: { nameVi: string; nameEn: string; address: string; region: string; rep: string; owner: string; tier: string; goods: string; status: string };
  interactions: DetailInteraction[];
  /** toàn bộ tương tác (tab Tương tác) */
  ixRows: IxRow[];
  todos: DetailTodo[];
  owners: DetailPerson[];
  contacts: DetailPerson[];
  contactTotal: number;
  /** danh bạ đầy đủ (tab Liên hệ) */
  contactRows: ContactRow[];
  tabCounts: { contacts: number; jobs: number; interactions: number; myHana: number; wiki: number };
  /** tab Wiki & tệp: ghi chú nghiệp vụ + thư viện tệp (board WK_INIT) */
  wikiNotes: WikiNote[];
  wikiFiles: WikiFile[];
  /** tab Hợp đồng & cấu hình */
  contracts: CfgContract[];
  znsRows: ZnsRow[];
  /** thẻ Cấu hình riêng (cột phải) */
  cfgRows: [string, string][];
  freeTime: { k: string; dem: number; det: number; std: string }[];
  feeCodes: string[];
  banks: BankRow[];
  bkgMain: string;
  bkgBackup: string;
  cs: string;
  /** vượt hạn mức → đang giữ DO */
  holdDo: boolean;
  /** hoá đơn chưa thu (tab Công nợ) — số tiền chỉ người có quyền xem */
  invoices: InvoiceRow[];
  /** người dùng myHANA (tab myHANA) */
  myUsers: MyUserRow[];
}

export interface MyUserRow {
  name: string;
  mail: string;
  scope: string;
  status: 'Đang dùng' | 'Chờ xác minh' | 'Chờ BOD duyệt';
  last: string;
}

export interface InvoiceRow {
  no: string;
  job: string;
  due: string;
  late: string;
  tone: 'warn' | 'err' | 'lb';
}

const MONTHS: [string, number | null][] = [
  ['Oct', 372], ['Nov', 380], ['Dec', 425], ['Jan', 360], ['Feb', null], ['Mar', 410],
  ['Apr', 432], ['May', 412], ['Jun', 455], ['Jul', 398], ['Aug', 520], ['Sep', 486],
];

const GREENBUD: CustomerDetailData = {
  id: 'GREENBUD',
  code: 'KH-0412',
  group: 'Thuỷ sản · đông lạnh',
  since: '03/Mar/2023',
  source: 'Giới thiệu · Busan Fresh Co.',
  portCommon: 'Cát Lái · Cái Mép',
  carrierCommon: 'KMTC · HMM · YML',
  updated: '22/Oct/2026 09:40',
  updatedBy: 'DAVID',
  tags: ['RF', 'Hàn Quốc', 'Khách VIP', 'Thanh toán chậm'],
  jobsTotal: 8,
  jobs90: 27,
  jobs: [
    { code: 'SE260639198', kind: 'RF', route: 'CLI → BUS', box: '1×40RF', etd: '25/Oct/2026', status: 'BK Confirmed', sale: 'DA', pic: 'TU' },
    { code: 'SE260639186', kind: 'RF', route: 'CLI → BUS', box: '2×40RF', etd: '25/Oct/2026', status: 'BK Standby', sale: 'DA', pic: 'TU' },
    { code: 'SE260639171', kind: 'RF', route: 'CLI → LAX', box: '1×40RF', etd: '21/Oct/2026', status: 'BL Released', sale: 'DA', pic: 'TU' },
    { code: 'SE260639142', kind: 'RF', route: 'CLI → SIN', box: '1×40RF', etd: '14/Oct/2026', status: 'BL Released', sale: 'DA', pic: 'TU' },
    { code: 'ST260639066', kind: 'TR', route: 'Cà Mau → CLI', box: '2×40RF', etd: '19/Oct/2026', status: 'BL Released', sale: 'DA', pic: 'TU' },
  ],
  jobRows: [
    { code: 'SE260639198', kind: 'RF', route: 'CLI → BUS', cnee: 'Busan Fresh Co.', vessel: 'KMTC HOCHIMINH 2210', box: '1×40RF', etd: '25/Oct/2026', status: 'BK Confirmed', sale: 'DAVID 70% · TUANNT 30%' },
    { code: 'SE260639186', kind: 'RF', route: 'CLI → BUS', cnee: 'Busan Fresh Co.', vessel: 'KMTC HOCHIMINH 2210', box: '2×40RF', etd: '25/Oct/2026', status: 'BK Standby', sale: 'DAVID 70% · TUANNT 30%' },
    { code: 'SE260639171', kind: 'RF', route: 'CLI → LAX', cnee: 'Pacific Apparel LLC', vessel: 'YM CERTAINTY 089N', box: '1×40RF', etd: '21/Oct/2026', status: 'BL Released', sale: 'DAVID 70% · TUANNT 30%' },
    { code: 'SE260639142', kind: 'RF', route: 'CLI → SIN', cnee: 'Lion Home Pte', vessel: 'WAN HAI 316 S041', box: '1×40RF', etd: '14/Oct/2026', status: 'BL Released', invoiced: true, sale: 'DAVID 70% · TUANNT 30%' },
    { code: 'ST260639066', kind: 'TR', route: 'Cà Mau → CLI', cnee: 'Kho lạnh Cát Lái', vessel: 'Xe 51C-882.14', box: '2×40RF', etd: '19/Oct/2026', status: 'BL Released', sale: 'DAVID 70% · TUANNT 30%' },
    { code: 'SE260637412', kind: 'RF', route: 'CLI → BUS', cnee: 'Busan Fresh Co.', vessel: 'HMM 0118E', box: '2×40RF', etd: '28/Sep/2026', status: 'BL Released', invoiced: true, sale: 'DAVID' },
    { code: 'SE260637377', kind: 'RF', route: 'CLI → RTM', cnee: 'Nordhout BV', vessel: 'MSC RAYA 2243W', box: '1×40RF', etd: '16/Sep/2026', status: 'BL Released', invoiced: true, sale: 'DAVID' },
    { code: 'SE260637301', kind: 'RF', route: 'CLI → BUS', cnee: 'Busan Fresh Co.', vessel: 'KMTC 2207', box: '1×40RF', etd: '05/Sep/2026', status: 'Cancelled', sale: 'DAVID' },
  ],
  refunds: [
    { job: 'SE260637412', what: 'Hoàn cược cont · HMM', state: 'ready' },
    { job: 'SE260637377', what: 'Hoàn DEM thu thừa · MSC', state: 'wait' },
    { job: 'SE260636990', what: 'Hoàn cược cont · KMTC', state: 'done' },
  ],
  routes: [
    { lane: 'CLI → BUS · RF', carriers: 'KMTC · HMM', jobs: 27, teu: 46 },
    { lane: 'CLI → LAX · RF', carriers: 'YML · ONE', jobs: 11, teu: 18 },
    { lane: 'CLI → SIN · RF', carriers: 'Wan Hai', jobs: 8, teu: 14 },
    { lane: 'CLI → RTM · RF', carriers: 'MSC', jobs: 6, teu: 10 },
    { lane: 'Cà Mau → CLI · trucking', carriers: 'Xe nhà', jobs: 7, teu: 12 },
  ],
  exports: MONTHS.map(([month, value]) => ({ month, value })),
  exportTotal: '4,65',
  exportDelta: '+8,0%',
  hanaShare: '47%',
  declarations: 96,
  lots: 104,
  legal: {
    nameVi: 'CÔNG TY TNHH XUẤT NHẬP KHẨU GREEN BUD',
    nameEn: 'GREEN BUD IMPORT EXPORT COMPANY LIMITED',
    address: 'KCN Khánh An, U Minh, Cà Mau',
    region: 'Nội địa · Việt Nam · Cà Mau',
    rep: 'Lê Văn Hải · Tổng giám đốc',
    owner: 'Lê Văn Hải',
    tier: '2 · Quan trọng · BCO · chủ hàng',
    goods: 'Thuỷ sản đông lạnh · RF',
    status: 'Đang hoạt động · tra 01/Oct/2026',
  },
  interactions: [
    { channel: 'Zalo', who: 'Phạm Ngọc Thảo', at: '22/Oct/2026 08:52', by: 'DAVID', text: 'Hỏi cut-off SI lô SE260639198 qua Zalo OA · DAVID trả lời sau 6 phút · Đã xem.' },
    { channel: 'ZNS', who: 'Phạm Ngọc Thảo', at: '21/Oct/2026 15:10', by: 'Tự động', text: 'ZNS-BKC · Booking đã xác nhận SE260639198 · Đã xem.' },
    { channel: 'ZNS', who: 'Nguyễn Hoàng Phúc', at: '20/Oct/2026 17:20', by: 'Tự động', text: 'ZNS-ETD · Đổi ETD SE260639186 · Lỗi: người nhận chặn tin từ OA.', failed: true },
  ],
  ixRows: [
    { kind: 'Zalo', who: 'Phạm Ngọc Thảo', note: 'Hỏi cut-off SI lô SE260639198 qua Zalo OA · DAVID trả lời sau 6 phút · Đã xem.', at: '22/Oct/2026 08:52', by: 'DAVID' },
    { kind: 'ZNS', who: 'Phạm Ngọc Thảo', note: 'ZNS-BKC · Booking đã xác nhận SE260639198 · Đã xem.', at: '21/Oct/2026 15:10', by: 'Tự động' },
    { kind: 'ZNS', who: 'Nguyễn Hoàng Phúc', note: 'ZNS-ETD · Đổi ETD SE260639186 · Lỗi: người nhận chặn tin từ OA.', at: '20/Oct/2026 17:20', by: 'Tự động', fu: 'Follow: gửi email thay', fuTone: 'warn' },
    { kind: 'Mail MKT', who: 'Phạm Ngọc Thảo', note: 'Chiến dịch MKT-6UHT · cập nhật giá RF tháng 11 · đã mở 2 lần, bấm link lịch tàu.', at: '20/Oct/2026 08:31', by: 'Hệ thống' },
    { kind: 'Quà', who: 'Lê Văn Hải', note: 'Đợt GC-260912-1316 MOONCAKE · đã giao (DELIVERED).', at: '14/Sep/2026 10:20', by: 'DAVID' },
    { kind: 'Gọi', who: 'Phạm Ngọc Thảo', note: 'Khách cần thêm 2×40RF đi Busan tuần 44, hỏi giá KMTC so với HMM.', at: '22/Oct/2026 09:40', by: 'DAVID', fu: 'Follow: gửi báo giá trước 23/Oct/2026 12:00', fuTone: 'warn' },
    { kind: 'Mail', who: 'Trần Thị Mai Anh', note: 'Gửi SOA tháng 9, khách hẹn thanh toán 2 hoá đơn quá hạn vào 25/Oct.', at: '21/Oct/2026 15:12', by: 'LIENNTK', fu: 'Follow: kiểm tiền về 25/Oct/2026', fuTone: 'lb' },
    { kind: 'Gặp', who: 'Lê Văn Hải', note: 'Gặp tại nhà máy Cà Mau: kế hoạch Q1/2027 tăng 30% sản lượng đi Hàn, muốn hạn mức 700 tr.', at: '15/Oct/2026 10:00', by: 'DAVID' },
    { kind: 'Gọi', who: 'Nguyễn Hoàng Phúc', note: 'Xác nhận draft B/L SE260639171, sửa tên notify party.', at: '13/Oct/2026 14:25', by: 'HOATTN' },
    { kind: 'Mail', who: 'Phạm Ngọc Thảo', note: 'Đã gửi lịch tàu RF tháng 11 · 3 hãng.', at: '08/Oct/2026 08:30', by: 'TUANNT' },
  ],
  todos: [
    { title: 'Vượt hạn mức · đang giữ DO', owner: 'DAVID', due: 'Hôm nay', action: 'Công nợ' },
    { title: 'Yêu cầu thanh toán hoàn phí · SE260637412', owner: 'DAVID', due: 'Hãng đã hoàn · chờ trả khách', action: 'Yêu cầu' },
  ],
  owners: [
    { name: 'DAVID', role: 'Sale · 70% · theo khách' },
    { name: 'MAITTN', role: 'CS · theo khách' },
    { name: 'AnNPH', role: 'Sale Asst · theo khách' },
    { name: 'LIENNTK', role: 'Kế toán · theo khách' },
    { name: 'TUANNT', role: 'Sale · 30% · theo khách' },
  ],
  contacts: [
    { name: 'Phạm Ngọc Thảo', role: 'Logistics · đầu mối booking' },
    { name: 'Trần Thị Mai Anh', role: 'Kế toán trưởng' },
    { name: 'Nguyễn Hoàng Phúc', role: 'Chứng từ XNK' },
  ],
  contactTotal: 5,
  contactRows: [
    { name: 'Phạm Ngọc Thảo', role: 'Logistics · đầu mối booking', phone: '0909 ••• 203', mail: 'thao.p••••@minhhaiseafood.vn', zalo: 'follow', flags: ['BKCF', 'B/L', 'AN'] },
    { name: 'Trần Thị Mai Anh', role: 'Kế toán trưởng', phone: '0918 ••• 760', mail: 'maianh.k••••@minhhaiseafood.vn', zalo: 'follow', flags: ['Debit', 'Hoá đơn'] },
    { name: 'Nguyễn Hoàng Phúc', role: 'Chứng từ XNK', phone: '0937 ••• 905', mail: 'phuc.d••••@minhhaiseafood.vn', zalo: 'unfollow', flags: ['BKCF', 'B/L', 'AN'] },
    { name: 'Lê Văn Hải', role: 'Tổng giám đốc', owner: true, phone: '0903 ••• 112', mail: 'hai.l••••@minhhaiseafood.vn', zalo: 'none', flags: [] },
    { name: 'Võ Thị Kim Ngân', role: 'QC · lịch đóng hàng', phone: '0976 ••• 418', mail: 'ngan.q••••@minhhaiseafood.vn', zalo: 'follow', flags: ['AN'] },
  ],
  tabCounts: { contacts: 5, jobs: 8, interactions: 10, myHana: 2, wiki: 3 },
  wikiNotes: [{ title: 'Đóng hàng RF', body: 'Kho Cà Mau chỉ nhận cont trước 14:00; cắm điện trước 4 giờ trước khi đóng.', meta: 'PHUONGNKT · 15/Oct/2026' }],
  wikiFiles: [
    { name: 'GREENBUD-GPKD.pdf', kind: 'Giấy phép ĐKKD', size: '640 KB', meta: 'DAVID · 03/Mar/2023' },
    { name: 'GREENBUD-PIF-2026.xlsx', kind: 'Partner Info Form', size: '85 KB', meta: 'DAVID · 10/Jan/2026' },
    { name: 'HANA-HD-2023-031-signed.pdf', kind: 'Hợp đồng', size: '2,1 MB', meta: 'DAVID · 03/Mar/2023' },
  ],
  contracts: [
    { name: 'HĐ dịch vụ HANA-HD-2023-031', desc: 'Ký 03/Mar/2023 · hiệu lực đến 02/Mar/2027', status: 'Hiệu lực', ok: true },
    { name: 'Phụ lục 02 · free time RF', desc: 'Ký 15/Jan/2026', status: 'Hiệu lực', ok: true },
    { name: 'Phụ lục 03 · hạn mức 700 tr', desc: 'Gửi khách 16/Oct/2026', status: 'Chờ khách ký', ok: false },
  ],
  znsRows: [
    { key: 'bkc', name: 'Booking xác nhận', tid: 'ZNS-BKC', tpl: 'ok', on: true, who: ['Phạm Ngọc Thảo', 'Nguyễn Hoàng Phúc'] },
    { key: 'etd', name: 'ETD / ETA đổi', tid: 'ZNS-ETD', tpl: 'ok', on: true, who: ['Phạm Ngọc Thảo'] },
    { key: 'an', name: 'Arrival Notice', tid: 'ZNS-AN', tpl: 'ok', on: false, who: ['Phạm Ngọc Thảo'] },
    { key: 'dlv', name: 'Giao hàng xong', tid: 'ZNS-DLV', tpl: 'wait', on: false, who: ['Võ Thị Kim Ngân'] },
    { key: 'ar', name: 'Nhắc công nợ', tid: 'ZNS-AR', tpl: 'rej', on: false, acc: true, who: ['Trần Thị Mai Anh'] },
  ],
  cfgRows: [
    ['Hạn mức tín dụng', '•••• · duyệt 02/Jan/2026 · [BOD-DIR] PHILIP (thay ghế ACC-CA trống)'],
    ['Điều khoản thanh toán', '30 ngày sau hoá đơn'],
    ['Tiền tệ hoá đơn', 'VND · tỷ giá bán VCB ngày ETD'],
    ['Gửi hoá đơn tới', 'Trần Thị Mai Anh · Kế toán trưởng'],
  ],
  freeTime: [
    { k: 'KMTC · 40RF', dem: 5, det: 4, std: '3 / 3' },
    { k: 'HMM · 40RF', dem: 5, det: 5, std: '4 / 3' },
    { k: 'YML · 40RF', dem: 4, det: 4, std: '3 / 3' },
  ],
  feeCodes: ['Phí CIC', 'Phí cắm điện RF', 'Phí kiểm dịch'],
  banks: [{ bank: 'VCB · Vietcombank', no: '0451000123456', holder: 'GREEN BUD IMPORT EXPORT COMPANY LIMITED', isDefault: true, verified: true, by: 'LIENNTK', verifiedBy: 'NGOCANH · 12/Sep/2026' }],
  bkgMain: 'PHUONGNKT',
  bkgBackup: 'PhuongHT',
  cs: 'HOATTN',
  holdDo: true,
  invoices: [
    { no: 'HĐ 0004128', job: 'SE260637412', due: '28/Sep/2026', late: 'Quá 24 ngày', tone: 'warn' },
    { no: 'HĐ 0004102', job: 'SE260637377', due: '21/Sep/2026', late: 'Quá 31 ngày', tone: 'err' },
    { no: 'HĐ 0004215', job: 'SE260639142', due: '13/Nov/2026', late: 'Còn 22 ngày', tone: 'lb' },
    { no: 'HĐ 0004222', job: 'ST260639066', due: '18/Nov/2026', late: 'Còn 27 ngày', tone: 'lb' },
    { no: 'HĐ 0004090', job: 'SE260637301', due: '14/Sep/2026', late: 'Huỷ · phí cont rỗng 0,0', tone: 'lb' },
  ],
  myUsers: [
    { name: 'Phạm Ngọc Thảo', mail: 'thao.p••••@minhhaiseafood.vn', scope: 'Tracking + chứng từ', status: 'Đang dùng', last: 'Đăng nhập 22/Oct/2026 08:12' },
    { name: 'Trần Thị Mai Anh', mail: 'maianh.k••••@minhhaiseafood.vn', scope: 'Hoá đơn + SOA', status: 'Đang dùng', last: 'Đăng nhập 20/Oct/2026 16:40' },
  ],
};

/** Hồ sơ chi tiết của một khách. Khách chưa có dữ liệu mẫu riêng: dựng tối thiểu từ dòng danh sách (không job, không tương tác). */
export function detailFor(c: Customer): CustomerDetailData {
  if (c.id === GREENBUD.id) return GREENBUD;
  return {
    ...GREENBUD,
    id: c.id,
    code: '—',
    group: '—',
    since: '—',
    source: '—',
    portCommon: '—',
    carrierCommon: '—',
    updated: c.updated,
    updatedBy: c.updatedBy,
    tags: [],
    jobsTotal: c.jobs,
    jobs90: c.jobs,
    jobs: [],
    routes: [],
    jobRows: [],
    refunds: [],
    exports: [],
    legal: { ...GREENBUD.legal, nameVi: c.id, nameEn: '—', address: c.province, region: `${c.region} · ${c.country} · ${c.province}`, rep: '—', owner: '—', goods: '—', status: '—' },
    interactions: [],
    ixRows: [],
    todos: [],
    owners: [{ name: c.sale, role: 'Sale · theo khách' }],
    contacts: [],
    contactTotal: c.contactCount,
    contactRows: [],
    tabCounts: { contacts: c.contactCount, jobs: c.jobs, interactions: 0, myHana: 0, wiki: 0 },
    wikiNotes: [],
    wikiFiles: [],
    contracts: [],
    znsRows: [],
    cfgRows: [],
    freeTime: [],
    feeCodes: [],
    banks: [],
    bkgMain: c.bkg,
    bkgBackup: c.bkg2,
    cs: '—',
    holdDo: false,
    invoices: [],
    myUsers: [],
  };
}

/* Tab Hiệu suất (board: H167) — số job theo tháng, tính từ chuỗi 12 tháng của board. */
export type PerfPeriod = '3M' | '6M' | '1Y' | 'YTD';
export const PERF_PERIODS: PerfPeriod[] = ['3M', '6M', '1Y', 'YTD'];
const PERF_MONTHS = ['Nov/25', 'Dec/25', 'Jan/26', 'Feb/26', 'Mar/26', 'Apr/26', 'May/26', 'Jun/26', 'Jul/26', 'Aug/26', 'Sep/26', 'Oct/26'];
const PERF_JOBS = [7, 9, 6, 4, 8, 10, 9, 11, 8, 10, 9, 12];
const PERF_LANES: [string, number, string][] = [
  ['CLI → BUS · RF', 0.46, 'KMTC · HMM'],
  ['CLI → LAX · RF', 0.18, 'YML · ONE'],
  ['CLI → SIN · RF', 0.14, 'Wan Hai'],
  ['CLI → RTM · RF', 0.1, 'MSC'],
  ['Cà Mau → CLI · trucking', 0.12, 'Xe nhà'],
];

export interface PerfData {
  range: string;
  months: { m: string; n: number; teu: number }[];
  prev: number[] | null;
  jobs: number;
  prevJobs: number;
  lanes: { k: string; carriers: string; jobs: number; teu: number; w: number }[];
  aria: string;
  laneAria: string;
}

export function perfFor(period: PerfPeriod, empty: boolean): PerfData {
  const nM = period === '3M' ? 3 : period === '6M' ? 6 : period === '1Y' ? 12 : 10;
  const jm = empty ? PERF_JOBS.map(() => 0) : PERF_JOBS;
  const cur = jm.slice(-nM);
  const prevP = jm.slice(-2 * nM, -nM);
  const sum = (a: number[]) => a.reduce((x, y) => x + y, 0);
  const jobs = sum(cur);
  const prevJobs = sum(prevP);
  const first = PERF_MONTHS[12 - nM]!;
  const months = cur.map((n, i) => ({ m: PERF_MONTHS[12 - nM + i]!, n, teu: Math.round(n * 1.7) }));
  const hasPrev = prevP.length === nM && prevJobs > 0;
  const lanes = PERF_LANES.map(([k, r, carriers]) => ({ k, carriers, jobs: Math.round(jobs * r), teu: Math.round(jobs * r * 1.7), w: Math.round((r / 0.46) * 100) }));
  return {
    range: `${period === 'YTD' ? 'Jan/2026' : first.replace('/', '/20')} – Oct/2026 · so với ${hasPrev ? `${nM} tháng trước` : 'kỳ trước (chưa có)'}`,
    months,
    prev: hasPrev ? prevP : null,
    jobs,
    prevJobs,
    lanes,
    aria: `Job theo tháng ${first} – Oct/26: ${cur.join(', ')} · tổng ${jobs} job, ${Math.round(jobs * 1.7)} TEU${hasPrev ? ` · kỳ trước ${prevJobs} job` : ''}`,
    laneAria: `Job theo tuyến: ${lanes.map((l) => `${l.k} ${l.jobs}`).join(', ')}`,
  };
}
